/**
 * SitePerformanceWidget — comparative safety performance across sites.
 *
 * Reuses /v2/comparison (which resolves the user's ACCESSIBLE sites server-side
 * and computes each metric per site), so a site-restricted user only ever sees
 * their own rows and cannot widen the table by editing a request.
 *
 * Clicking a row focuses the whole dashboard on that site. That is a page-local
 * scope override, deliberately NOT the global site switcher: focusing a row to
 * read one plant's numbers should not re-issue the session's JWT or change what
 * every other page shows. The chip above the table says what is focused and
 * clears it.
 *
 * Cross-site by nature, so it ignores a single selected site — but it does honour
 * the time and dimension filters.
 */
import React from 'react';
import { ArrowRight, X } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { fetchComparison } from '../api/client';
import { formatMetricValue } from '../utils/format';
import { CARD, HEADING } from '../ui';

export default function SitePerformanceWidget({
  title = 'Site / Plant Performance',
  metrics = [],
  onFocusSite: onFocusSiteProp,
  focusedSiteId: focusedSiteIdProp = null,
}) {
  const {
    module, time, dimensions, standard, refreshMs, focusSiteId, onFocusSite: onFocusSiteCtx,
  } = useDashboard();
  // The page owns the focus state and supplies it through the widget context
  // (widgets are built from a static catalog, so it cannot arrive as a prop);
  // explicit props still win, for a page that renders this directly.
  const onFocusSite = onFocusSiteProp || onFocusSiteCtx;
  const focusedSiteId = focusedSiteIdProp ?? focusSiteId ?? null;
  const timeKey = time ? JSON.stringify(time) : '';
  const dimsKey = dimensions ? JSON.stringify(dimensions) : '';

  const { data, loading, error } = useDashboardData(
    (signal) => {
      const body = { module, metrics };
      if (time) body.time = time;
      if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
      if (standard) body.standard = standard;
      return fetchComparison(body, { signal });
    },
    [module, metrics.join(','), timeKey, dimsKey, standard],
    { enabled: !!module && metrics.length > 0, refreshMs },
  );

  const cols = data?.metrics || [];
  const sites = data?.sites || [];
  const clickable = typeof onFocusSite === 'function';
  const focusedName = sites.find((s) => s.site_id === focusedSiteId)?.site_name;

  return (
    <div className={`${CARD} p-4`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className={HEADING}>{title}</h3>
        {focusedSiteId && (
          <button
            type="button"
            onClick={() => onFocusSite(null)}
            className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs
                       font-medium text-blue-700 hover:bg-blue-100"
            title="Clear the site focus"
          >
            Focused: {focusedName || focusedSiteId}
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {loading && <div className="h-40 animate-pulse rounded bg-gray-100" />}
      {!loading && error && (
        <div className="py-8 text-center text-sm text-red-600">Could not load site comparison.</div>
      )}

      {!loading && !error && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-400">
                <th className="py-2 pr-3 font-semibold">Site</th>
                {cols.map((c) => (
                  <th key={c.key} className="py-2 px-2 text-right font-semibold">{c.label}</th>
                ))}
                {clickable && <th className="py-2 pl-2" aria-label="Focus" />}
              </tr>
            </thead>
            <tbody>
              {sites.length === 0 && (
                <tr>
                  <td colSpan={cols.length + 1} className="py-8 text-center text-gray-400">
                    No sites available for the selected filters.
                  </td>
                </tr>
              )}
              {sites.map((s) => {
                const focused = s.site_id === focusedSiteId;
                return (
                  <tr
                    key={s.site_id}
                    onClick={clickable ? () => onFocusSite(focused ? null : s.site_id) : undefined}
                    className={`border-b border-gray-100 last:border-0 ${
                      clickable ? 'cursor-pointer hover:bg-blue-50/60' : ''
                    } ${focused ? 'bg-blue-50/80' : ''}`}
                    title={clickable ? 'Focus the dashboard on this site' : undefined}
                  >
                    <td className="py-2 pr-3 font-medium text-gray-800">{s.site_name}</td>
                    {cols.map((c) => (
                      <td key={c.key} className="py-2 px-2 text-right tabular-nums text-gray-700">
                        {formatMetricValue(s.values?.[c.key], c.format)}
                      </td>
                    ))}
                    {clickable && (
                      <td className="py-2 pl-2 text-gray-300">
                        <ArrowRight className="h-4 w-4" />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {clickable && sites.length > 0 && (
            <p className="mt-2 text-[11px] text-gray-500">
              Click a row to focus every panel on that site; click it again, or the chip
              above, to go back to all sites.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
